import { useCallback, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

export type UseRadioGroupProps = {
  name?: string;
  /** Pass `null` to indicate "no selection" in controlled mode */
  value?: string | number | null;
  defaultValue?: string | number | null;
  onChange?:
    | ((value: string | number) => void)
    | Dispatch<SetStateAction<string | number>>; // Support both callback and setState
  isDisabled?: boolean;
  isNative?: boolean;
} & Record<string, unknown>;

export function useRadioGroup(props?: UseRadioGroupProps) {
  const {
    name,
    value: valueProp,
    defaultValue,
    onChange,
    isDisabled,
    isNative,
    ...rest
  } = props || {};

  const [internalValue, setInternalValue] = useState<string | number | null>(
    defaultValue ?? null
  );
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const handleChange = useCallback(
    (nextValue: string | number) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onChange?.(nextValue);
    },
    [isControlled, onChange]
  );

  /**
   * Clear the visual selection without triggering onChange.
   * Use this when you need to programmatically uncheck all options
   * (e.g., when switching to a custom input) without implying the user made a selection.
   */
  const clearSelection = useCallback(() => {
    if (!isControlled) {
      setInternalValue(null);
    }
  }, [isControlled]);

  const getRootProps = useCallback(
    (additionalProps: Record<string, unknown> = {}) => ({
      ...additionalProps,
      role: "radiogroup",
      ...rest,
    }),
    [rest]
  );

  const getRadioProps = useCallback(
    (radioProps: Record<string, unknown> = {}) => {
      const { value: radioValue, ...otherRadioProps } = radioProps as {
        value?: string | number;
      } & Record<string, unknown>;
      return {
        ...otherRadioProps,
        name,
        isChecked: radioValue === value,
        onChange: () => {
          if (radioValue === undefined) return;
          handleChange(radioValue);
        },
        isDisabled,
        value: radioValue,
      };
    },
    [name, value, handleChange, isDisabled]
  );

  return {
    name,
    value,
    onChange: handleChange,
    setValue: handleChange,
    clearSelection,
    getRootProps,
    getRadioProps,
    isDisabled,
    isNative,
  };
}
